/**
 * What the contact form and the feedback controls send to POST /api/feedback,
 * and the one validator both sides use: the page checks a submission with it
 * before sending, so people see the same messages the Worker would answer with,
 * and the Worker checks it again because anyone can post to the API.
 */

import { GAME_SLUGS } from '../game-slugs';

export type Reaction = 'up' | 'down';

/** Where a piece of game feedback was given. */
export type FeedbackSource = 'feedback-button' | 'game-over';

export const CONTACT_SUBJECTS = [
  'Bug report',
  'Game request',
  'Support request',
  'Partnership opportunity',
  'Other',
] as const;

export type ContactSubject = (typeof CONTACT_SUBJECTS)[number];

export const LIMITS = {
  name: 100,
  email: 254,
  messageMin: 10,
  message: 1000,
} as const;

/**
 * Every submission carries an id the page makes up (a UUID). Sending the same id
 * again updates that submission instead of adding another, which is how a
 * game-over thumbs down gets its "what could be better?" note attached, and why
 * a retried request is never counted twice.
 */
export interface ContactSubmission {
  kind: 'contact';
  id: string;
  name: string;
  email: string;
  subject: ContactSubject;
  message: string;
}

export interface FeedbackSubmission {
  kind: 'feedback';
  id: string;
  source: FeedbackSource;
  /** The game's slug, as in src/games.data.ts. */
  game: string;
  reaction?: Reaction;
  message?: string;
}

export type Submission = ContactSubmission | FeedbackSubmission;

export type SubmissionField = 'kind' | 'id' | 'name' | 'email' | 'subject' | 'message' | 'source' | 'game' | 'reaction';

export type ParseResult =
  | { ok: true; submission: Submission }
  | { ok: false; field: SubmissionField; error: string };

/** The name of the hidden field that people never see and bots fill in. */
export const HONEYPOT_FIELD = 'website';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed string, or undefined when the value is missing, not a string, or blank. */
function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function isGameSlug(value: string): boolean {
  return GAME_SLUGS.some((slug) => slug === value);
}

function isSubject(value: string): value is ContactSubject {
  return CONTACT_SUBJECTS.some((subject) => subject === value);
}

function fail(field: SubmissionField, error: string): ParseResult {
  return { ok: false, field, error };
}

function parseContact(input: Record<string, unknown>, id: string): ParseResult {
  const name = text(input.name);
  if (!name) return fail('name', 'Enter your name.');
  if (name.length > LIMITS.name) return fail('name', `Keep your name under ${LIMITS.name} characters.`);

  const email = text(input.email);
  if (!email) return fail('email', 'Enter your email address, so we can reply.');
  if (email.length > LIMITS.email || !EMAIL.test(email)) return fail('email', 'Enter a valid email address.');

  const subject = text(input.subject);
  if (!subject || !isSubject(subject)) return fail('subject', 'Choose what your message is about.');

  const message = text(input.message);
  if (!message || message.length < LIMITS.messageMin) {
    return fail('message', `Write a little more: at least ${LIMITS.messageMin} characters.`);
  }
  if (message.length > LIMITS.message) return fail('message', `Keep it under ${LIMITS.message} characters.`);

  return { ok: true, submission: { kind: 'contact', id, name, email, subject, message } };
}

function parseFeedback(input: Record<string, unknown>, id: string): ParseResult {
  const source = input.source;
  if (source !== 'feedback-button' && source !== 'game-over') return fail('source', 'Unknown feedback source.');

  const game = text(input.game);
  if (!game || !isGameSlug(game)) return fail('game', 'Unknown game.');

  const reaction = input.reaction;
  if (reaction !== undefined && reaction !== 'up' && reaction !== 'down') return fail('reaction', 'Unknown reaction.');

  const message = text(input.message);
  if (message && message.length > LIMITS.message) return fail('message', `Keep it under ${LIMITS.message} characters.`);
  if (!reaction && !message) return fail('message', 'Pick a thumb or write a few words.');

  return { ok: true, submission: { kind: 'feedback', id, source, game, reaction, message } };
}

export function parseSubmission(input: unknown): ParseResult {
  if (!isRecord(input)) return fail('kind', 'Expected a JSON object.');

  const id = input.id;
  if (typeof id !== 'string' || !UUID.test(id)) return fail('id', 'Expected a UUID id.');

  if (input.kind === 'contact') return parseContact(input, id.toLowerCase());
  if (input.kind === 'feedback') return parseFeedback(input, id.toLowerCase());
  return fail('kind', 'Unknown kind of submission.');
}

/** True when the honeypot field was filled in, which only bots do. */
export function isHoneypotFilled(input: unknown): boolean {
  return isRecord(input) && typeof input[HONEYPOT_FIELD] === 'string' && input[HONEYPOT_FIELD] !== '';
}
